import type { AvatarConfig } from "@shared/types";

export const CLASS_PROTOCOL_VERSION = 1;

export type ClassMode = "lobby" | "quizzle" | "word-runner" | "draw-it";

export interface ClassStudent {
  id: string;
  name: string;
  avatarConfig: AvatarConfig;
}

export interface ClassRoomState {
  code: string;
  className: string;
  hostId: string;
  roster: ClassStudent[];
  mode: ClassMode;
  activityCode?: string;
  onlineIds: string[];
  rev?: number;
}

export type ClassEvent =
  | { type: "state"; state: ClassRoomState }
  | { type: "error"; message: string };

export interface ClassJoinOpts {
  code: string;
  role: "teacher" | "student";
  /** teacher only */
  className?: string;
  hostId?: string;
  /** student only */
  studentId?: string;
  name?: string;
  avatarConfig?: AvatarConfig;
}

export interface ClassClient {
  join(opts: ClassJoinOpts): Promise<ClassRoomState>;
  /** Teacher: set the class mode (lobby / launch an activity by room code). */
  setMode(mode: ClassMode, activityCode?: string): void;
  backToLobby(): void;
  removeStudent(studentId: string): void;
  on(cb: (e: ClassEvent) => void): () => void;
  leave(): void;
}

const CODE_KEY = "wr_class_code";
const STUDENT_KEY_PREFIX = "wr_class_student_"; // + code

export function rememberClassCode(code: string) {
  try { localStorage.setItem(CODE_KEY, code.toUpperCase()); } catch { /* ignore */ }
}
export function getRememberedClassCode(): string {
  try { return (localStorage.getItem(CODE_KEY) || "").toUpperCase(); } catch { return ""; }
}
export function forgetClassCode() {
  try { localStorage.removeItem(CODE_KEY); } catch { /* ignore */ }
}
export function rememberStudent(code: string, studentId: string) {
  try { localStorage.setItem(STUDENT_KEY_PREFIX + code.toUpperCase(), studentId); } catch { /* ignore */ }
}
export function getRememberedStudent(code: string): string {
  try { return localStorage.getItem(STUDENT_KEY_PREFIX + code.toUpperCase()) || ""; } catch { return ""; }
}

export function newStudentId(): string {
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  return "stu_" + Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

// Local cache of {name, avatar} per student so the lobby can re-send them on
// (re)join without another roster fetch.
interface StudentRecord { name: string; avatar: AvatarConfig }
export function saveStudentRecord(code: string, id: string, name: string, avatar: AvatarConfig) {
  try { localStorage.setItem(`wr_class_rec_${code.toUpperCase()}_${id}`, JSON.stringify({ name, avatar })); } catch { /* ignore */ }
}
export function getStudentRecord(code: string, id: string): StudentRecord | null {
  try {
    const raw = localStorage.getItem(`wr_class_rec_${code.toUpperCase()}_${id}`);
    return raw ? (JSON.parse(raw) as StudentRecord) : null;
  } catch { return null; }
}

const SKIN = ["#ffd9b3", "#f2c99e", "#e8b088", "#c68642", "#8d5524"];
const SHIRT = ["#ff5fa7", "#6bd0ff", "#6be09b", "#ffd166", "#a855f7", "#ff8a3d", "#ef4444"];
const PANTS = ["#2563eb", "#1f2937", "#7c3aed", "#0d7a4f", "#b91c1c", "#0891b2"];
const pick = <T,>(a: T[]) => a[Math.floor(Math.random() * a.length)]!;
export function randomAvatar(): AvatarConfig {
  return { skinColor: pick(SKIN), shirtColor: pick(SHIRT), pantsColor: pick(PANTS) };
}

// Teacher's own class (code + name), remembered on the teacher device so they
// return to the same persistent class each day.
export function rememberTeacherClass(code: string, name: string) {
  try { localStorage.setItem("wr_class_teacher_code", code.toUpperCase()); localStorage.setItem("wr_class_teacher_name", name); } catch { /* ignore */ }
}
export function getTeacherClass(): { code: string; name: string } {
  try { return { code: (localStorage.getItem("wr_class_teacher_code") || "").toUpperCase(), name: localStorage.getItem("wr_class_teacher_name") || "" }; } catch { return { code: "", name: "" }; }
}
